/* global google */
import boroughMapping from '../components/Location/postCodeMappings.json';
import { loadGoogleMaps } from './loadGoogleMaps';

// How a typed place / postcode becomes the "local area" shown on profiles:
// a London postcode district → its borough (postCodeMappings.json), outside
// the UK the neighbourhood, otherwise the postal town. Shared by the profile
// location field (components/Location/NearestLocationField.js) and the
// quick-match hero, so both name places the same way.
export const getNeighborhoodInfo = (address, callback) => {
  const geocoder = new google.maps.Geocoder();

  geocoder.geocode({ 'address': address }, (results, status) => {
    if (status === 'OK') {
      const addressComponents = results[0].address_components;
      let localAreaInfo = null;

      // Check if the entered address matches any area name in the boroughMapping
      // London area names only for London addresses, and whole words only
      // — a plain "includes" turned Leeds into "Lee".
      const inLondon = /\bLondon\b/.test(address);
      const areaNameMatch = inLondon && Object.values(boroughMapping).find(areaName =>
        new RegExp(`\\b${areaName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`).test(address));
      if (areaNameMatch) {
        callback(areaNameMatch);
        return;
      }

      // Attempt to find the borough using the postcode
      const postcodeComponent = addressComponents.find(component =>
        component.types.includes('postal_code')
      );
      if (postcodeComponent) {
        const postcode = postcodeComponent.long_name.split(' ')[0]; // Get the first part of the postcode
        localAreaInfo = boroughMapping[postcode];
      }

      // If we found local area information, call the callback with it
      if (localAreaInfo) {
        callback(localAreaInfo);
      } else {
        // Outside the UK (e.g. Berlin): the neighbourhood — Wedding,
        // Charlottenburg, Friedrichshain… — like London's postcode areas,
        // rather than just the city.
        const country = addressComponents.find(c => c.types.includes('country'))?.short_name;
        if (country && country !== 'GB') {
          const hood = ['neighborhood', 'sublocality_level_2', 'sublocality_level_1', 'sublocality']
            .map(t => addressComponents.find(c => c.types.includes(t)))
            .find(Boolean);
          if (hood) {
            callback(hood.long_name.replace(/^Bezirk\s+/i, ''));
            return;
          }
        }
        // In London without a mapped postcode (e.g. "London Borough of
        // Lambeth, London" for a typed "Stockwell"): the borough, not "London".
        const londonBorough = addressComponents
          .map(c => c.long_name)
          .find(name => /^London Borough of /i.test(name) || /^Royal Borough of /i.test(name) || name === 'City of Westminster');
        if (londonBorough) {
          callback(londonBorough.replace(/^(London Borough of|Royal Borough of|City of)\s+/i, ''));
          return;
        }
        // Fallback to borough if postcode mapping is not found
        const boroughComponent = addressComponents.find(component =>
          component.types.includes('postal_town') || component.types.includes('locality')
        );
        if (boroughComponent) {
          localAreaInfo = boroughComponent.long_name;
        }

        if (localAreaInfo) {
          callback(localAreaInfo);
        } else {
          callback('Area not found');
        }
      }
    } else {
      callback('Area not found');
    }
  });
};

// Too broad to find people near: a whole country, a state / nation /
// region (e.g. "France", "Scotland", "California"), or a county (e.g.
// "Essex" — no town to name it by). Returns its name.
const BROAD_TYPES = ['country', 'administrative_area_level_1', 'administrative_area_level_2'];
const broadPlaceName = (result) => (
  (result?.types || []).some(t => BROAD_TYPES.includes(t))
    ? (result.name || result.address_components?.[0]?.long_name || result.formatted_address || '')
    : null
);
// { localArea: 'France', coordinates, broad: true, countryCode: 'fr' } —
// accepted as a location, then they're asked for a town (optional).
const broadPlace = (result, name) => {
  const loc = result.geometry.location;
  const countryCode = (result.address_components || []).find(c => (c.types || []).includes('country'))?.short_name;
  // A county is small enough to search near its middle (the normal
  // radius) if they don't pick a town in it.
  const county = !(result.types || []).some(t => t === 'country' || t === 'administrative_area_level_1');
  return { localArea: name, coordinates: [loc.lng(), loc.lat()], broad: true, county, countryCode: countryCode ? countryCode.toLowerCase() : undefined };
};

// A place picked from Google's autocomplete list → { localArea, coordinates }
// (with broad: true for a whole country / region).
export const localAreaFromPlace = async (place) => {
  const loc = place?.geometry?.location;
  const address = place?.formatted_address || place?.name;
  if (!loc || !address) return null;
  const broad = broadPlaceName(place);
  if (broad) return broadPlace(place, broad);
  const localArea = await new Promise((resolve) => getNeighborhoodInfo(address, resolve));
  if (!localArea || localArea === 'Area not found') return null;
  return { localArea, coordinates: [loc.lng(), loc.lat()] };
};

// Look up a typed town / postcode, or [lng, lat] from the browser, and
// return { localArea, coordinates: [lng, lat] } — or null if not found.
// countryCode keeps a typed town to that country (the "which town in
// France?" step).
export const findLocalArea = async ({ query, coords, countryCode }) => {
  await loadGoogleMaps();
  const geocoder = new google.maps.Geocoder();
  const request = coords
    ? { location: { lat: coords[1], lng: coords[0] } }
    : { address: String(query || '').trim(), ...(countryCode ? { componentRestrictions: { country: countryCode } } : {}) };
  const results = await new Promise((resolve) => {
    geocoder.geocode(request, (res, status) => resolve(status === 'OK' ? res : null));
  });
  const hit = results?.[0];
  if (!hit?.geometry) return null;
  const broad = !coords && broadPlaceName({ ...hit, name: hit.address_components?.[0]?.long_name });
  if (broad) return broadPlace(hit, broad);
  // A typed London area name (e.g. "Stockwell") is already the answer —
  // Google tends to resolve those to the whole borough.
  const typed = String(query || '').trim();
  const typedArea = typed && /\bLondon\b/.test(hit.formatted_address)
    && Object.values(boroughMapping).find(name => name.toLowerCase() === typed.toLowerCase());
  const localArea = typedArea || await new Promise((resolve) => getNeighborhoodInfo(hit.formatted_address, resolve));
  if (!localArea || localArea === 'Area not found') return null;
  const loc = hit.geometry.location;
  return { localArea, coordinates: coords || [loc.lng(), loc.lat()] };
};
